"use client";

import { useEffect, useState, useCallback } from "react";
import {
  History,
  Loader2,
  FileText,
  Calendar,
  Download,
  FileOutput,
  UserPlus,
  X,
  Eye,
} from "lucide-react";
import { jsPDF } from "jspdf";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";
import { saveAs } from "file-saver";
import { cn } from "@/lib/utils";

type BilanLogSummary = {
  patientName?: string;
  patientAge?: number;
  birthdate?: string | null;
  hasConclusion?: boolean;
};

type BilanLogItem = {
  id: string;
  processId: string;
  fileName: string;
  processStartedAt: string;
  totalSteps: number;
  successCount: number;
  errorCount: number;
  summary?: BilanLogSummary;
};

export function BilanHistory() {
  const [logs, setLogs] = useState<BilanLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noticeLogId, setNoticeLogId] = useState<string | null>(null);
  const [noticeContent, setNoticeContent] = useState<string | null>(null);
  const [loadingNotice, setLoadingNotice] = useState(false);
  const [addToPatientLogId, setAddToPatientLogId] = useState<string | null>(null);
  const [patients, setPatients] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch("/api/bilan-logs?withSummary=1&limit=50")
      .then((r) => {
        if (!r.ok) throw new Error("Erreur chargement");
        return r.json();
      })
      .then((data) => setLogs(data.logs ?? []))
      .catch((e) => setError(String(e.message)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

  const openNotice = (id: string, patientName?: string) => {
    setNoticeLogId(id);
    setNoticeContent(null);
    setLoadingNotice(true);
    fetch(`/api/bilan-logs/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("Erreur"))))
      .then((data) => {
        const payload = data.payload ?? data;
        const steps = Array.isArray(payload?.steps) ? payload.steps : [];
        const deanonymizeStep = steps.find((s: any) => s.stepId === "deanonymize");
        const deanonymizeFlash = steps.find((s: any) => s.stepId === "deanonymize-flash");

        const deanonymizePro = steps.find((s: any) => s.stepId === "deanonymize-pro");
        const selectionStep = steps.find((s: any) => s.stepId === "selection-proposition");

        let finalConclusion = null;
        if (selectionStep?.output && typeof selectionStep.output === "object") {
          const selectedId = (selectionStep.output as { selected?: string }).selected;
          const choiceStep = steps.find((s: any) => s.stepId === `deanonymize-${selectedId}`);
          finalConclusion = (choiceStep?.output as any)?.finalConclusion;
        }

        if (!finalConclusion) {
          finalConclusion = (deanonymizeStep?.output as any)?.finalConclusion ||
            (deanonymizeFlash?.output as any)?.finalConclusion ||
            (deanonymizePro?.output as any)?.finalConclusion;
        }
        if (finalConclusion && patientName) finalConclusion = finalConclusion.replace(/\[CODE\]/g, patientName);
        setNoticeContent(finalConclusion || "Aucune conclusion enregistrée.");
      })
      .catch(() => setNoticeContent("Impossible de charger la notice."))
      .finally(() => setLoadingNotice(false));
  };

  const getExtractionAndConclusion = useCallback(
    async (id: string, patientName?: string): Promise<{ originalText: string; finalConclusion: string } | null> => {
      const res = await fetch(`/api/bilan-logs/${id}`);
      if (!res.ok) return null;
      const data = await res.json();
      const payload = data.payload ?? data;
      const steps = Array.isArray(payload?.steps) ? payload.steps : [];
      const extractionStep = steps.find((s: { stepId?: string }) => s.stepId === "extraction");
      const deanonymizeStep = steps.find((s: any) => s.stepId === "deanonymize");
      const deanonymizeFlash = steps.find((s: any) => s.stepId === "deanonymize-flash");
      const deanonymizePro = steps.find((s: any) => s.stepId === "deanonymize-pro");
      const selectionStep = steps.find((s: any) => s.stepId === "selection-proposition");

      const originalText =
        extractionStep?.output && typeof extractionStep.output === "object"
          ? (extractionStep.output as { textSentToNextSteps?: string }).textSentToNextSteps ?? ""
          : "";

      let finalConclusion = "";

      if (selectionStep?.output && typeof selectionStep.output === "object") {
        const selectedId = (selectionStep.output as { selected?: string }).selected;
        const choiceStep = steps.find((s: any) => s.stepId === `deanonymize-${selectedId}`);
        finalConclusion = (choiceStep?.output as any)?.finalConclusion || "";
      }

      if (!finalConclusion) {
        finalConclusion = (deanonymizeStep?.output as any)?.finalConclusion ||
          (deanonymizeFlash?.output as any)?.finalConclusion ||
          (deanonymizePro?.output as any)?.finalConclusion || "";
      }
      if (patientName) finalConclusion = finalConclusion.replace(/\[CODE\]/g, patientName);
      return { originalText, finalConclusion };
    },
    []
  );

  const downloadPdf = async (item: BilanLogItem) => {
    const data = await getExtractionAndConclusion(item.id, item.summary?.patientName);
    if (!data) return;
    const pdf = new jsPDF();
    const margin = 20;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const maxWidth = pageWidth - margin * 2;
    let y = 20;
    const lines = pdf.splitTextToSize(data.originalText || "", maxWidth);
    for (const line of lines) {
      if (y > 270) {
        pdf.addPage();
        y = 20;
      }
      pdf.text(line, margin, y);
      y += 5;
    }
    y += 10;
    if (y > 250) {
      pdf.addPage();
      y = 20;
    }
    pdf.setFont("helvetica", "bold");
    pdf.text("CONCLUSION", margin, y);
    y += 10;
    pdf.setFont("helvetica", "normal");
    const conclLines = pdf.splitTextToSize(data.finalConclusion || "", maxWidth);
    for (const line of conclLines) {
      if (y > 270) {
        pdf.addPage();
        y = 20;
      }
      pdf.text(line, margin, y);
      y += 5;
    }
    const dateStr = item.processStartedAt.slice(0, 10);
    pdf.save(`bilan_${item.fileName.replace(/\.[^/.]+$/, "")}_${dateStr}.pdf`);
  };

  const downloadWord = async (item: BilanLogItem) => {
    const data = await getExtractionAndConclusion(item.id, item.summary?.patientName);
    if (!data) return;
    const originalParagraphs = (data.originalText || "")
      .split("\n")
      .map((line) => new Paragraph({ children: [new TextRun({ text: line, size: 22 })] }));
    const conclusionParagraphs = (data.finalConclusion || "")
      .split("\n")
      .map((line) => new Paragraph({ children: [new TextRun({ text: line, size: 22 })] }));
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            ...originalParagraphs,
            new Paragraph({ children: [] }),
            new Paragraph({ children: [] }),
            new Paragraph({
              children: [new TextRun({ text: "CONCLUSION", bold: true, size: 28 })],
              heading: HeadingLevel.HEADING_1,
            }),
            new Paragraph({ children: [] }),
            ...conclusionParagraphs,
          ],
        },
      ],
    });
    const blob = await Packer.toBlob(doc);
    const dateStr = item.processStartedAt.slice(0, 10);
    saveAs(blob, `bilan_${item.fileName.replace(/\.[^/.]+$/, "")}_${dateStr}.docx`);
  };

  const openAddToPatient = (id: string) => {
    setAddToPatientLogId(id);
    setSelectedPatientId(null);
    fetch("/api/patients")
      .then((r) => (r.ok ? r.json() : { patients: [] }))
      .then((data) => setPatients(data.patients ?? []))
      .catch(() => setPatients([]));
  };

  const confirmAddToPatient = async () => {
    if (!addToPatientLogId || !selectedPatientId) return;
    const res = await fetch(`/api/bilan-logs/${addToPatientLogId}`);
    if (!res.ok) return;
    const data = await res.json();
    const payload = data.payload ?? data;
    const steps = Array.isArray(payload?.steps) ? payload.steps : [];
    const deanonymizeStep = steps.find((s: any) => s.stepId === "deanonymize");
    const deanonymizeFlash = steps.find((s: any) => s.stepId === "deanonymize-flash");
    const deanonymizePro = steps.find((s: any) => s.stepId === "deanonymize-pro");
    const selectionStep = steps.find((s: any) => s.stepId === "selection-proposition");

    let finalConclusion = "";
    if (selectionStep?.output && typeof selectionStep.output === "object") {
      const selectedId = (selectionStep.output as { selected?: string }).selected;
      const choiceStep = steps.find((s: any) => s.stepId === `deanonymize-${selectedId}`);
      finalConclusion = (choiceStep?.output as any)?.finalConclusion || "";
    }

    if (!finalConclusion) {
      finalConclusion = (deanonymizeStep?.output as any)?.finalConclusion ||
        (deanonymizeFlash?.output as any)?.finalConclusion ||
        (deanonymizePro?.output as any)?.finalConclusion || "";
    }
    const item = logs.find((l) => l.id === addToPatientLogId);
    const selectedPatient = patients.find((p) => p.id === selectedPatientId);
    const patientDisplayName = selectedPatient ? `${selectedPatient.lastName} ${selectedPatient.firstName}` : item?.summary?.patientName;
    if (patientDisplayName) finalConclusion = finalConclusion.replace(/\[CODE\]/g, patientDisplayName);
    const title = item ? `Bilan ${item.fileName} – ${formatDate(item.processStartedAt)}` : "Bilan";
    setAdding(true);
    try {
      const postRes = await fetch(`/api/patients/${selectedPatientId}/reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          content: finalConclusion,
          reportDate: item?.processStartedAt?.slice(0, 10) ?? undefined,
          originalFileName: item?.fileName,
        }),
      });
      if (postRes.ok) {
        setAddToPatientLogId(null);
        setSelectedPatientId(null);
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900 mb-4">
        <History className="h-5 w-5 text-primary-600" />
        Historique des bilans
      </h2>
      <p className="text-sm text-slate-500 mb-4">
        Consultez les bilans déjà générés, téléchargez les documents ou ajoutez la notice au dossier patient.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        </div>
      ) : error ? (
        <p className="text-sm text-amber-600 py-4">{error}</p>
      ) : logs.length === 0 ? (
        <p className="text-sm text-slate-500 py-4">Aucun bilan enregistré.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-600 font-medium">
                <th className="pb-3 pr-4">Patient</th>
                <th className="pb-3 pr-4">Âge</th>
                <th className="pb-3 pr-4">Date</th>
                <th className="pb-3 pr-4">Fichier</th>
                <th className="pb-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((item) => (
                <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="py-3 pr-4 font-medium text-slate-900">
                    {item.summary?.patientName ?? "—"}
                  </td>
                  <td className="py-3 pr-4 text-slate-600">
                    {item.summary?.patientAge != null ? `${item.summary.patientAge} ans` : item.summary?.birthdate ?? "—"}
                  </td>
                  <td className="py-3 pr-4 text-slate-600 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                    {formatDate(item.processStartedAt)}
                  </td>
                  <td className="py-3 pr-4 text-slate-600 truncate max-w-[180px]" title={item.fileName}>
                    {item.fileName}
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => openNotice(item.id, item.summary?.patientName)}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                        title="Voir la notice"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadPdf(item)}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                        title="Télécharger PDF"
                      >
                        <FileOutput className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadWord(item)}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                        title="Télécharger Word"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => openAddToPatient(item.id)}
                        className="p-2 rounded-lg hover:bg-primary-50 text-slate-600 hover:text-primary-600"
                        title="Ajouter au dossier patient"
                      >
                        <UserPlus className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Notice */}
      {noticeLogId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary-600" />
                Notice – Résultat du bilan
              </h3>
              <button
                type="button"
                onClick={() => { setNoticeLogId(null); setNoticeContent(null); }}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingNotice ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
                </div>
              ) : (
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{noticeContent}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajouter au patient */}
      {addToPatientLogId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary-600" />
              Ajouter au dossier patient
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Choisissez le patient auquel associer la notice (conclusion) de ce bilan.
            </p>
            <select
              value={selectedPatientId ?? ""}
              onChange={(e) => setSelectedPatientId(e.target.value || null)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 mb-4"
            >
              <option value="">Sélectionner un patient</option>
              {patients.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.lastName} {p.firstName}
                </option>
              ))}
            </select>
            {patients.length === 0 && (
              <p className="text-xs text-slate-500 mb-4">Aucun patient en base. Créez-en un depuis la page Patients.</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setAddToPatientLogId(null); setSelectedPatientId(null); }}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmAddToPatient}
                disabled={!selectedPatientId || adding}
                className={cn(
                  "px-4 py-2 rounded-lg font-medium text-white",
                  selectedPatientId && !adding ? "bg-primary-500 hover:bg-primary-600" : "bg-slate-300 cursor-not-allowed"
                )}
              >
                {adding ? "Ajout..." : "Ajouter"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
